import { useState, useCallback } from 'react';
import { RotateCcw, History } from 'lucide-react';
import { SlideOverPanel } from '@/components/admin/shared/SlideOverPanel';
import { FrameworkHistoryTab } from './FrameworkHistoryTab';
import {
  useFrameworkDeliveryPlan,
  useFrameworkPlanVersions,
  useRestoreFrameworkPlanVersion,
} from '@/hooks/useFrameworkDeliveryPlan';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

interface VersionsSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function VersionsSidePanel({ isOpen, onClose }: VersionsSidePanelProps) {
  const [activeTab, setActiveTab] = useState<'structure' | 'allocations'>('structure');

  return (
    <SlideOverPanel isOpen={isOpen} onClose={onClose} title="Version History" width="max-w-2xl">
      {/* Tab toggle */}
      <div className="px-5 pt-4 pb-0 border-b border-gray-200">
        <div className="flex gap-1">
          {(['structure', 'allocations'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'px-3 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors',
                activeTab === tab
                  ? 'border-xtravirt-navy text-xtravirt-navy'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {tab === 'structure' ? 'Framework Versions' : 'Plan Versions'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {activeTab === 'structure' ? (
          <FrameworkHistoryTab onSwitchToFramework={onClose} />
        ) : (
          <PlanVersionsContent />
        )}
      </div>
    </SlideOverPanel>
  );
}

/** Plan delivery plan version history - extracted from FrameworkPlanHistorySubTab */
function PlanVersionsContent() {
  const { data: planData } = useFrameworkDeliveryPlan();
  const { data: versionsData, isLoading } = useFrameworkPlanVersions();
  const restore = useRestoreFrameworkPlanVersion();

  const versions = versionsData?.versions || [];
  const currentVersion = planData?.version;

  const handleRestore = useCallback(
    async (versionId: string, versionNumber: number) => {
      if (
        !window.confirm(
          `Restore the framework delivery plan to version ${versionNumber}? The current state will be saved as a new version first, so this action is reversible.`
        )
      )
        return;
      try {
        await restore.mutateAsync(versionId);
      } catch (err) {
        console.error('Failed to restore framework version:', err);
        window.alert('Failed to restore. Check the console for details.');
      }
    },
    [restore]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p>No version history yet</p>
        <p className="text-sm mt-1">Changes will be tracked when you save the framework delivery plan.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Every save and restore of the framework delivery plan creates a snapshot here.
      </p>
      {versions.map((version) => (
        <div
          key={version.id}
          className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">Version {version.version}</span>
                {currentVersion !== undefined && version.version === currentVersion - 1 && (
                  <span className="badge badge-green text-xs">Most recent</span>
                )}
              </div>
              {version.changeDescription && (
                <p className="text-sm text-gray-600 mt-1">{version.changeDescription}</p>
              )}
              <div className="text-xs text-gray-500 mt-2">
                {new Date(version.createdAt).toLocaleString()}
                {version.createdByEmail && ` · ${version.createdByEmail}`}
              </div>
              {version.changedFields && version.changedFields.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {version.changedFields.map((field: string, idx: number) => (
                    <span
                      key={idx}
                      className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => handleRestore(version.id, version.version)}
              className="btn-secondary btn-sm"
              disabled={restore.isPending}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Restore
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
